import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { useAddresses } from '@/api/addresses/queries/useAddresses'
import { PkgShippingMethod, UserPkg } from '@/api/user-package/types/UserPkg'
import { AppDialog } from '@/components/AppDialog'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { Button } from '@/components/ui/button'
import { ShippingPrices } from '@/helpers/priceChart'
import { RoutePath } from '@/RoutePath'

import EditAddressDialog from '../Profile/components/EditAddressDialog'
import { AddressSelector } from './components/AddressSelector'
import { OptionCard } from './components/OptionCard'
import { ShippingMethodSelector } from './components/ShippingMethodSelector'
export interface ShippingOptions {
  selectedAddressId: string
  selectedShippingMethod: PkgShippingMethod
  isInsuranceAdded: boolean
}

interface Props {
  pkg: UserPkg
  shippingOptions: ShippingOptions
  onUpdateShippingOptions: (options: Partial<ShippingOptions>) => void
  shippingPrices: ShippingPrices
  insurancePrice: number
}

export default function ShippingPage({ pkg, shippingOptions, onUpdateShippingOptions, shippingPrices, insurancePrice }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [showNoInsuranceConfirm, setShowNoInsuranceConfirm] = useState(false)
  const { pkgId } = useParams<{ pkgId: string }>()
  const [showInsuranceModal, setShowInsuranceModal] = useState(false)
  const { data: addresses, isLoading: isLoadingAddresses } = useAddresses()
  const [showAddAddress, setShowAddAddress] = useState(false)

  const handleContinue = async () => {
    if (!shippingOptions.selectedAddressId && !addresses?.length) {
      setShowAddAddress(true)
      return
    }
    const selectedAddress = addresses?.find(addr => addr.id === shippingOptions.selectedAddressId)
    if (!selectedAddress) {
      setShowAddAddress(true)
      return
    }
    if (!shippingOptions.isInsuranceAdded) {
      setShowNoInsuranceConfirm(true)
    } else {
      // Navigate to payment summary page
      navigate(`${RoutePath.PaymentSummary}/${pkgId}`)
    }
  }

  const handleAddressCreated = (createdAddress?: { id: string }) => {
    if (createdAddress) {
      onUpdateShippingOptions({ selectedAddressId: createdAddress.id })
    }
  }

  return (
    <div className='space-y-6'>
      {/* Title Section */}
      <div>
        <h1 className='text-text-primary mb-2 text-2xl font-medium'>{t('payment.selectShippingTitle')}</h1>
        <p className='text-text-secondary text-sm leading-relaxed'>{t('payment.selectShippingDesc')}</p>
      </div>

      {/* Address Selection */}
      <div className='space-y-4'>
        <h3 className='text-text-primary text-sm font-medium'>{t('payment.selectAddress')}</h3>
        <AddressSelector
          onAddAddress={() => setShowAddAddress(true)}
          selectedAddressId={shippingOptions.selectedAddressId}
          onSelectAddress={addressId => onUpdateShippingOptions({ selectedAddressId: addressId })}
        />
      </div>

      {/* Shipping Method Selection */}
      <div className='space-y-4'>
        <h3 className='text-text-primary text-sm font-medium'>{t('payment.selectShippingType')}</h3>
        <ShippingMethodSelector
          selectedMethod={shippingOptions.selectedShippingMethod}
          onSelectMethod={method => onUpdateShippingOptions({ selectedShippingMethod: method })}
          packageValue={pkg.valueInUSD || 0}
          shippingPrices={shippingPrices}
        />
      </div>

      {/* Additional Options */}
      <div className='space-y-4'>
        <h3 className='text-text-primary text-sm font-medium'>{t('payment.additionalOptions')}</h3>
        <OptionCard
          onSelect={() => onUpdateShippingOptions({ isInsuranceAdded: !shippingOptions.isInsuranceAdded })}
          selected={shippingOptions.isInsuranceAdded}
          title={t('payment.addInsuranceTitle')}
          description={
            <p>
              {t('payment.insuranceOptionDescription')}{' '}
              <Button variant='link' className='text-sm font-normal' size='sm' onClick={() => setShowInsuranceModal(true)}>
                {t('payment.addInsuranceMoreInfo')}
              </Button>
            </p>
          }
          endContent={insurancePrice > 0 ? <p>${insurancePrice.toFixed(2)}</p> : null}
        />
      </div>

      {/* Continue Button */}
      <div className='space-y-4'>
        <Button onClick={handleContinue} disabled={isLoadingAddresses} className='w-full' size='lg'>
          {!shippingOptions.selectedAddressId && !addresses?.length ? t('payment.addAddressOrContinue') : t('payment.continueToSummary')}
        </Button>
      </div>

      {/* Insurance Info Modal */}
      <AppDialog isOpen={showInsuranceModal} title={t('payment.insuranceModalTitle')} onOpenChange={setShowInsuranceModal}>
        <div className='text-text-secondary space-y-4'>
          <p>{t('payment.insuranceModalIntro')}</p>
          <div>
            <p className='font-medium'>{t('payment.insuranceAllows')}</p>
            <ul className='mt-2 space-y-1 text-sm'>
              <li>• {t('payment.insuranceBullet1')}</li>
              <li>• {t('payment.insuranceBullet2')}</li>
              <li>• {t('payment.insuranceBullet3')}</li>
            </ul>
          </div>
          <div>
            <p className='font-medium'>{t('payment.insuranceAttention')}</p>
            <p className='text-sm'>{t('payment.insuranceFragileNote')}</p>
          </div>
          <div className='flex flex-col items-center gap-2 pt-6'>
            <p className='text-xs'>
              *{t('payment.insuranceTerms')}
              <br />
              {t('payment.insuranceKnowMore')}{' '}
              <a
                href='https://www.shiptanbul.com/knowledge-center/faqs/insurance'
                target='_blank'
                rel='noreferrer'
                className='text-text-link hover:underline'
              >
                {t('payment.insuranceClickHere')}
              </a>
            </p>
            <Button onClick={() => setShowInsuranceModal(false)} variant='outline'>
              {t('common.close')}
            </Button>
          </div>
        </div>
      </AppDialog>
      <EditAddressDialog isOpen={showAddAddress} onOpenChange={setShowAddAddress} editingAddress={null} onSuccess={handleAddressCreated} />
      <ConfirmDialog
        isOpen={showNoInsuranceConfirm}
        onClose={() => setShowNoInsuranceConfirm(false)}
        onConfirm={() => {
          setShowNoInsuranceConfirm(false)
          navigate(`${RoutePath.PaymentSummary}/${pkgId}`)
        }}
        confirmText={t('payment.continueWithoutInsurance')}
        cancelText={t('common.back')}
        title={t('payment.continueWithoutInsuranceTitle')}
        content={t('payment.continueWithoutInsuranceContent')}
      />
    </div>
  )
}
